import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark-dimmed.css';
import { DocsImage } from './docs-image';
import { DocsLink } from './docs-link';

interface HeadingEntry {
  id: string;
  text: string;
  level: number;
}

interface Props {
  source: string;
}

export function DocsContent({ source }: Props) {
  const [headings, setHeadings] = useState<HeadingEntry[]>([]);

  // Scroll to anchor on initial load once the article is rendered.
  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      el.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, [source]);

  useEffect(() => {
    const nodes = Array.from(
      document.querySelectorAll<HTMLElement>('article.docs-article :where(h2[id], h3[id])'),
    );
    const next = nodes.map((n) => ({
      id: n.id,
      text: n.textContent ?? '',
      level: n.tagName === 'H2' ? 2 : 3,
    }));
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHeadings(next);
  }, [source]);

  const components = useMemo(
    () => ({
      img: DocsImage,
      a: DocsLink,
    }),
    [],
  );

  return (
    <div className="mx-auto flex w-full max-w-5xl gap-8">
      <article className="docs-article prose dark:prose-invert min-w-0 flex-1">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[
            rehypeSlug,
            [rehypeAutolinkHeadings, { behavior: 'append', properties: { className: ['docs-anchor'], ariaHidden: 'true', tabIndex: -1 } }],
            rehypeHighlight,
          ]}
          components={components}
        >
          {source}
        </ReactMarkdown>
      </article>
      <nav className="sticky top-20 hidden h-fit w-56 shrink-0 text-sm md:block">
        <p className="mb-2 font-semibold text-muted-foreground">On this page</p>
        {headings.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No sections</p>
        ) : (
          <ol className="space-y-1">
            {headings.map((h) => (
              <li key={h.id} className={h.level === 3 ? 'pl-4' : ''}>
                <a
                  href={`#${h.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById(h.id);
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      history.replaceState(null, '', `#${h.id}`);
                    }
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  {h.text}
                </a>
              </li>
            ))}
          </ol>
        )}
      </nav>
    </div>
  );
}
